import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Newsletter } from '../components/Newsletter';
import { FinancialTerms } from '../components/FinancialTerms';
import { motion } from 'framer-motion';
import { Search, Calendar, User, Clock, ArrowRight, Tag, BookOpen, Heart, Info, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  tags: string[];
  image?: string;
}

const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'Understanding Compound Interest',
    excerpt: 'A deep dive into how compound interest works and why it\'s often called the eighth wonder of the world.',
    content: `# Understanding Compound Interest

    Compound interest is the interest you earn on both your initial investment (principal) and previously accumulated interest. Unlike simple interest, which only pays interest on your principal, compound interest helps your money grow faster by reinvesting your earnings.

    ## How It Works
    1. Initial Investment: You deposit money into an interest-bearing account
    2. First Interest Payment: You earn interest on your principal
    3. Compounding Begins: Future interest is earned on both principal and accumulated interest
    4. Growth Acceleration: The process repeats, creating exponential growth over time

    ### Real-World Example
    Let's say you invest $10,000 at 5% annual interest, compounded monthly:
    - After 1 year: $10,511.62
    - After 5 years: $12,833.59
    - After 10 years: $16,470.09
    - After 30 years: $43,219.42

    #### Benefits
    - Accelerated wealth building
    - Passive income generation
    - Long-term financial security
    - Tax-advantaged growth in retirement accounts

    #### Risks and Considerations
    - Requires long-term commitment
    - Market volatility can affect returns
    - Inflation may reduce real returns

    ### Market Impact
    Compound interest is fundamental to:
    - Stock market returns
    - Bond investments
    - Retirement planning
    - Debt accumulation (negative compounding)

    ### Historical Context
    The S&P 500's average annual return of 10.5% since 1957 demonstrates compound interest in action:
    - $10,000 invested in 1957
    - Worth approximately $4.2 million today
    - 98% of gains from compounding

    ### Implementation Tips
    1. Start early to maximize compounding time
    2. Reinvest dividends and interest payments
    3. Make regular contributions
    4. Choose appropriate investment vehicles
    5. Consider tax implications

    ### Comparison with Other Growth Methods
    | Method | Key Feature | Growth Pattern |
    |--------|-------------|----------------|
    | Compound Interest | Earnings on earnings | Exponential |
    | Simple Interest | Principal only | Linear |
    | Dividend Growth | Regular payments | Variable |

    ### Best For
    Compound interest is most effective for:
    - Long-term investors
    - Retirement savers
    - Young investors
    - Patient wealth builders`,
    author: 'Sarah Johnson',
    date: '2024-02-15',
    readTime: '8 min',
    category: 'Investing',
    tags: ['compound interest', 'investing', 'wealth building', 'financial planning'],
    image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80'
  },
  {
    id: '2',
    title: 'Asset Allocation Fundamentals',
    excerpt: 'Learn how to build a balanced investment portfolio through proper asset allocation strategies.',
    content: `# Asset Allocation Fundamentals

    Asset allocation is the strategic distribution of your investments across different asset classes to balance risk and reward according to your goals, risk tolerance, and investment timeline.

    ## Core Concepts
    1. Risk Management: Spreading investments across multiple asset classes
    2. Diversification: Reducing portfolio volatility
    3. Rebalancing: Maintaining target allocations
    4. Time Horizon: Matching investments to goals

    ### Real-World Example
    A moderate portfolio might look like:
    - 60% Stocks (Growth potential)
    - 30% Bonds (Stability)
    - 10% Cash (Liquidity)

    #### Benefits
    - Reduced portfolio volatility
    - Better risk management
    - Consistent long-term returns
    - Goal-aligned investing

    #### Risks and Considerations
    - Market correlation
    - Rebalancing costs
    - Tax implications
    - Individual asset risks

    ### Market Impact
    Asset allocation affects:
    - Portfolio performance
    - Risk management
    - Investment returns
    - Market exposure

    ### Statistical Support
    Research shows:
    - 90% of portfolio return variability comes from asset allocation
    - Properly diversified portfolios can reduce risk by up to 40%
    - Regular rebalancing can add 0.5% to annual returns

    ### Implementation Steps
    1. Determine risk tolerance
    2. Set investment goals
    3. Choose asset classes
    4. Select specific investments
    5. Monitor and rebalance

    ### Asset Class Comparison
    | Asset Class | Risk Level | Expected Return | Purpose |
    |------------|------------|-----------------|----------|
    | Stocks | High | Higher | Growth |
    | Bonds | Medium | Moderate | Income |
    | Cash | Low | Lower | Safety |

    ### Best For
    Asset allocation is crucial for:
    - Long-term investors
    - Retirement planning
    - Risk-conscious investors
    - Goal-based investing`,
    author: 'Michael Chen',
    date: '2024-02-20',
    readTime: '10 min',
    category: 'Portfolio Management',
    tags: ['asset allocation', 'investing', 'portfolio management', 'risk management'],
    image: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }
];

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  const categories = Array.from(new Set(blogPosts.map(post => post.category)));
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-7xl mx-auto">
        <Navigation />
        
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
              <BookOpen className="w-10 h-10 text-emerald-500" />
              Personal Finance Blog
            </h1>
            <p className="text-slate-400 text-lg">
              Insights and strategies for better financial management
            </p>
          </motion.div>
        </div>

        {/* Welcome Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="glass-card p-8 mb-12 text-center"
        >
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <Heart className="w-8 h-8 text-emerald-500" />
              </div>
            </div>
            <h2 className="text-2xl font-semibold mb-6">Welcome to Your Financial Journey</h2>
            <div className="space-y-6 text-slate-300">
              <p>
                Let's be honest - most of us weren't taught financial literacy in school. If you've ever felt overwhelmed by money management or confused by financial jargon, you're not alone. We've been there too, and that's exactly why we created this space.
              </p>
              <p>
                Here, you'll find clear, practical guidance without any judgment or complicated terminology. Whether you're just starting to budget or ready to dive into investing, we're committed to making financial education accessible to everyone. Our articles break down complex topics into actionable steps, helping you build confidence in your financial decisions.
              </p>
              <p>
                Join our community of learners and take control of your financial future. Start with any topic that interests you, and remember - every financial expert was once a beginner too. Ready to begin? Explore our resources below, and don't forget to share this knowledge with others who might benefit.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <button
                onClick={() => document.getElementById('articles')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-6 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium"
              >
                Explore Articles
              </button>
              <button
                onClick={() => document.getElementById('financial-terms')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-6 py-3 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors font-medium"
              >
                Learn Key Terms
              </button>
            </div>
          </div>
        </motion.div>

        {/* Financial Terms Section */}
        <div id="financial-terms" className="mb-12">
          <FinancialTerms />
        </div>

        {/* Newsletter - Top of page */}
        <div className="mb-12">
          <Newsletter 
            title="Subscribe to Financial Insights"
            subtitle="Get expert financial advice, market updates, and money-saving tips delivered to your inbox weekly."
          />
        </div>

        {/* Search and Filter */}
        <div id="articles" className="mb-8 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="glass-input px-4 py-2 rounded-lg md:w-48"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        {selectedPost ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Blog Post Detail */}
            <div className="glass-card p-8">
              <button
                onClick={() => setSelectedPost(null)}
                className="text-emerald-500 hover:text-emerald-400 mb-6 flex items-center gap-2"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to Articles
              </button>
              
              {selectedPost.image && (
                <img
                  src={selectedPost.image}
                  alt={selectedPost.title}
                  className="w-full h-[400px] object-cover rounded-lg mb-8"
                />
              )}
              
              <div className="flex flex-wrap gap-4 items-center mb-6">
                <span className="px-3 py-1 bg-emerald-500/10 text-emerald-500 rounded-full text-sm">
                  {selectedPost.category}
                </span>
                <div className="flex items-center gap-2 text-slate-400">
                  <User className="w-4 h-4" />
                  {selectedPost.author}
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Calendar className="w-4 h-4" />
                  {new Date(selectedPost.date).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Clock className="w-4 h-4" />
                  {selectedPost.readTime}
                </div>
              </div>

              <h1 className="text-3xl font-bold mb-6">{selectedPost.title}</h1>
              
              <div className="prose prose-invert max-w-none">
                {selectedPost.content.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4 text-slate-300 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 pt-8 border-t border-slate-700">
                <h3 className="text-lg font-semibold mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedPost.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-slate-800 text-slate-300 rounded-full text-sm"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Newsletter - Bottom of article */}
              <div className="mt-12">
                <Newsletter 
                  title="Enjoyed this article?"
                  subtitle="Subscribe to get more financial insights and tips delivered directly to your inbox."
                />
              </div>
            </div>
          </motion.div>
        ) : (
          /* Blog Post List */
          <div className="grid md:grid-cols-2 gap-6">
            {filteredPosts.map(post => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="glass-card p-6 cursor-pointer hover:scale-[1.02] transition-transform"
                onClick={() => setSelectedPost(post)}
              >
                {post.image && (
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                )}
                
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-emerald-500/10 text-emerald-500 rounded-full text-sm">
                    {post.category}
                  </span>
                  <span className="text-sm text-slate-400">
                    {post.readTime}
                  </span>
                </div>
                
                <h2 className="text-xl font-semibold mb-2">{post.title}</h2>
                <p className="text-slate-400 mb-4">{post.excerpt}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <User className="w-4 h-4" />
                    {post.author}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Calendar className="w-4 h-4" />
                    {new Date(post.date).toLocaleDateString()}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-8">
          <ContactForm />
        </div>
      </div>
    </div>
  );
};

export default Blog;